import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { tabClass } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Leaf } from "lucide-react";
import { GrassDetail } from "@/components/GrassDetail";

export default function GrassIdentifier() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState({ vernation: [], ligule: [] });
  const [q, setQ] = useState("");
  const [vern, setVern] = useState("");
  const [lig, setLig] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/grass/filters").then((r) => setFilters(r.data)); }, []);
  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (vern) p.set("vernation", vern);
    if (lig) p.set("ligule", lig);
    api.get(`/grass?${p.toString()}`).then((r) => setAll(r.data));
  }, [q, vern, lig]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-serif text-4xl sm:text-5xl text-emerald-950">Turf Identifier</h1>
        <p className="mt-2 text-stone-500 text-sm max-w-2xl">Identify South Florida turfgrass in the field by its physical markers, then open any species for its full disease, pest, and nutrient-deficiency watch guide.</p>

        <div className="mt-6 space-y-3">
          <Input data-testid="grass-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, vernation, seedhead (e.g., Y-shaped)…" className="rounded-full max-w-xl" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-[0.15em] text-stone-400">Vernation</span>
            <button data-testid="vern-any" onClick={() => setVern("")} className={tabClass(!vern)}>Any</button>
            {filters.vernation.map((v) => (
              <button key={v.value} data-testid={`vern-${v.value}`} onClick={() => setVern(v.value)} className={tabClass(vern === v.value)}>{v.label}</button>
            ))}
            <span className="ml-3 text-xs uppercase tracking-[0.15em] text-stone-400">Ligule</span>
            <button data-testid="lig-any" onClick={() => setLig("")} className={tabClass(!lig)}>Any</button>
            {filters.ligule.map((l) => (
              <button key={l.value} data-testid={`lig-${l.value}`} onClick={() => setLig(l.value)} className={tabClass(lig === l.value)}>{l.label}</button>
            ))}
          </div>
        </div>

        <p className="mt-5 text-xs text-stone-400" data-testid="grass-count">{all.length} varieties</p>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {all.map((g) => (
            <button key={g.id} data-testid={`grass-card-${g.id}`} onClick={() => setSelected(g)}
              className="text-left rounded-2xl border border-black/5 bg-white p-5 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:border-emerald-300 transition-colors">
              <div className="flex items-center gap-2 text-emerald-700"><Leaf className="w-4 h-4" /><span className="text-xs uppercase tracking-[0.15em]">{g.identification.vernation} vernation</span></div>
              <h3 className="mt-2 font-serif text-2xl text-emerald-950">{g.name}</h3>
              <p className="text-xs italic text-stone-500">{g.scientific_name}</p>
              <p className="mt-2 text-sm text-stone-600">{g.color_texture}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Badge variant="outline" className="rounded-full text-stone-500">🌾 {g.identification.seedhead_type}</Badge>
              </div>
            </button>
          ))}
          {all.length === 0 && <p className="text-sm text-stone-400">No varieties match those traits.</p>}
        </div>
      </div>

      {selected && <GrassDetail grass={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
